import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useCurrency } from '../context/CurrencyContext';
import { 
  Package, Clock, MapPin, ChevronRight, Search, Filter, MessageCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { OrderChat } from '../components/OrderChat';

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-700',
  confirmed: 'bg-blue-100 text-blue-700',
  preparing: 'bg-purple-100 text-purple-700',
  ready: 'bg-indigo-100 text-indigo-700',
  picked_up: 'bg-cyan-100 text-cyan-700',
  in_transit: 'bg-orange-100 text-orange-700',
  delivered: 'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-700',
};

export default function OrdersPage() {
  const { t } = useTranslation();
  const { api } = useAuth();
  const { formatPrice } = useCurrency();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [chatOrderId, setChatOrderId] = useState(null);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await api.get('/orders');
      setOrders(response.data);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredOrders = statusFilter === 'all' 
    ? orders 
    : orders.filter(order => order.status === statusFilter);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-slate-50 pt-24 pb-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900">{t('orders.title')}</h1>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40" data-testid="status-filter">
              <SelectValue placeholder={t('orders.allOrders')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('orders.allOrders')}</SelectItem>
              <SelectItem value="pending">{t('orders.status.pending')}</SelectItem>
              <SelectItem value="confirmed">{t('orders.status.confirmed')}</SelectItem>
              <SelectItem value="preparing">{t('orders.status.preparing')}</SelectItem>
              <SelectItem value="in_transit">{t('orders.status.in_transit')}</SelectItem>
              <SelectItem value="delivered">{t('orders.status.delivered')}</SelectItem>
              <SelectItem value="cancelled">{t('orders.status.cancelled')}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white rounded-2xl p-6 animate-pulse">
                <div className="h-6 bg-slate-200 rounded w-1/3 mb-4"></div>
                <div className="h-4 bg-slate-200 rounded w-1/2 mb-2"></div>
                <div className="h-4 bg-slate-200 rounded w-1/4"></div>
              </div>
            ))}
          </div>
        ) : filteredOrders.length > 0 ? (
          <div className="space-y-4">
            {filteredOrders.map((order) => (
              <Link
                key={order.id}
                to={`/order/${order.id}`}
                className="block bg-white rounded-2xl shadow-sm hover:shadow-md transition-shadow p-6"
                data-testid={`order-${order.id}`}
              >
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="font-semibold text-lg text-slate-900">
                      {order.restaurant_name}
                    </h3>
                    <p className="text-sm text-slate-500">
                      Order #{order.id.slice(0, 8).toUpperCase()}
                    </p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusColors[order.status]}`}>
                    {t(`orders.status.${order.status}`)}
                  </span>
                </div>

                <div className="flex items-center gap-4 text-sm text-slate-500 mb-4">
                  <div className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {formatDate(order.created_at)}
                  </div>
                  <div className="flex items-center gap-1">
                    <Package className="w-4 h-4" />
                    {order.items.length} {t('orders.items')}
                  </div>
                </div>

                <div className="flex items-center gap-2 text-sm text-slate-500 mb-4">
                  <MapPin className="w-4 h-4" />
                  {order.delivery_address}, {order.delivery_city}
                </div>

                <div className="flex justify-between items-center pt-4 border-t border-slate-100">
                  <div>
                    <span className="text-slate-500 text-sm">{t('orders.total')}</span>
                    <p className="font-bold text-lg text-orange-500">{formatPrice(order.total)}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {['pending', 'confirmed', 'preparing', 'ready', 'in_transit'].includes(order.status) && (
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="text-orange-500 border-orange-200 hover:bg-orange-50"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setChatOrderId(order.id);
                        }}
                        data-testid={`chat-${order.id}`}
                      >
                        <MessageCircle className="w-4 h-4" />
                      </Button>
                    )}
                    <Button variant="ghost" className="text-orange-500">
                      {t('orders.viewDetails')} <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="text-center py-16 bg-white rounded-2xl">
            <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-slate-100 flex items-center justify-center">
              <Package className="w-10 h-10 text-slate-400" />
            </div>
            <h2 className="text-xl font-semibold text-slate-900 mb-2">{t('orders.noOrders')}</h2>
            <p className="text-slate-500 mb-6">{t('orders.whenPlaceOrder')}</p>
            <Link to="/restaurants">
              <Button className="btn-primary">{t('cart.browseRestaurants')}</Button>
            </Link>
          </div>
        )}

        {/* Order Chat Modal */}
        <OrderChat 
          orderId={chatOrderId}
          isOpen={!!chatOrderId}
          onClose={() => setChatOrderId(null)}
          participants={{ customer: true, restaurant: true, driver: true }}
        />
      </div>
    </div>
  );
}
